import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, take, throwError } from 'rxjs';
import { ApiError } from '../../shared/interfaces/api-error.interface';
import { AuthService } from '../services/api/auth.service';
import { DialogService } from '../services/utils/dialog.service';

/**
 * Interceptor que maneja errores de API globalmente
 *
 * Casos manejados (con modal bloqueante):
 * - 0 (Sin conexión): Dialog informando el problema de red
 * - 403 FORBIDDEN: Dialog informando acceso denegado
 * - 410 GONE: Dialog + redirección al login (recurso expirado/no disponible)
 * - 423 LOCKED: Dialog informando cuenta bloqueada
 * - 429 TOO MANY REQUESTS: Dialog + redirección al login (exceso de intentos)
 * - 500 INTERNAL SERVER ERROR: Dialog informando error interno
 * - 502/504 (Gateway error): Dialog informando problemas de comunicación
 * - 503 (BFF no disponible): Dialog con mensaje de servicio no disponible
 *
 * Otros errores (400, 401, 404): Solo se propagan para manejo específico
 */
export const apiErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const dialogService = inject(DialogService);
  const authService = inject(AuthService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const apiError = error.error as ApiError;

      // Error de red (sin conexion)
      if (error.status === 0) {
        dialogService.noConnection$().subscribe(() => {
          router.navigate(['/404']);
        });
        return throwError(() => error);
      }

      // Error 410 GONE (recurso expirado o ya no disponible)
      if (error.status === 410) {
        dialogService.sessionExpired$().pipe(take(1)).subscribe(() => {
          authService.clearSession();
        });
        return throwError(() => error);
      }

      // Error 423 LOCKED (cuenta bloqueada)
      if (error.status === 423) {
        dialogService.accountLocked$(apiError?.message).subscribe();
        return throwError(() => error);
      }

      // Error 429 (Too many requests - bloqueo por exceso de intentos fallidos)
      if (error.status === 429) {
        dialogService
          .tooManyAttempts$(apiError?.message)
          .pipe(take(1))
          .subscribe(() => {
            authService.clearSession();
          });
        return throwError(() => error);
      }

      // Error 403 FORBIDDEN (acceso denegado)
      if (error.status === 403) {
        dialogService.accessDenied$(apiError?.message).subscribe(() => {
          router.navigate(['/404']);
        });
        return throwError(() => error);
      }

      // Error 500 (Error interno del servidor)
      if (error.status === 500) {
        dialogService.serverError$().subscribe(() => {
          router.navigate(['/404']);
        });
        return throwError(() => error);
      }

      // Error 502/504 (Errores de gateway - problemas de comunicación con el servidor)
      if (error.status === 502 || error.status === 504) {
        dialogService.gatewayError$().subscribe(() => {
          router.navigate(['/404']);
        });
        return throwError(() => error);
      }

      // Error 503 (Servicio no disponible - bloqueante)
      if (error.status === 503) {
        dialogService.serviceUnavailable$().subscribe(() => {
          router.navigate(['/404']);
        });
        return throwError(() => error);
      }

      // Propagar el error para que pueda ser manejado especificamente en los componentes
      return throwError(() => error);
    }),
  );
};
